import { useState, useEffect, useCallback, RefObject } from 'react';

interface UseReadingProgressOptions {
  /**
   * Reference to the scrollable container. If not provided, uses window.
   */
  containerRef?: RefObject<HTMLElement>;
  /**
   * Reference to the content element to track progress through.
   * If not provided, tracks progress through the entire document/container.
   */
  contentRef?: RefObject<HTMLElement>;
  /**
   * Throttle scroll event handling (in ms). Default: 16 (~60fps)
   */
  throttleMs?: number;
}

export function useReadingProgress(options: UseReadingProgressOptions = {}) {
  const { containerRef, contentRef, throttleMs = 16 } = options;
  const [progress, setProgress] = useState(0);

  const calculateProgress = useCallback(() => {
    const content = contentRef?.current;

    if (content) {
      // Calculate progress based on content element visibility
      const contentRect = content.getBoundingClientRect();
      const containerRect = containerRef?.current?.getBoundingClientRect() || {
        top: 0,
        height: window.innerHeight,
      };

      // How much of the content has scrolled past the top of the viewport
      const scrolledPast = containerRect.top - contentRect.top;
      // Total scrollable distance is content height minus viewport
      const totalScrollable = contentRect.height - containerRect.height;

      if (totalScrollable <= 0) {
        setProgress(100);
        return;
      }

      const percentage = Math.min(100, Math.max(0, (scrolledPast / totalScrollable) * 100));
      setProgress(percentage);
    } else {
      // Calculate progress based on scroll position
      const scrollTop = containerRef?.current?.scrollTop ?? window.scrollY;
      const scrollHeight =
        (containerRef?.current?.scrollHeight ?? document.documentElement.scrollHeight) -
        (containerRef?.current?.clientHeight ?? window.innerHeight);

      if (scrollHeight <= 0) {
        setProgress(100);
        return;
      }

      const percentage = Math.min(100, Math.max(0, (scrollTop / scrollHeight) * 100));
      setProgress(percentage);
    }
  }, [containerRef, contentRef]);

  useEffect(() => {
    let lastTime = 0;
    let rafId: number | null = null;

    const handleScroll = () => {
      const now = Date.now();
      if (now - lastTime >= throttleMs) {
        lastTime = now;
        calculateProgress();
      } else if (!rafId) {
        rafId = requestAnimationFrame(() => {
          lastTime = Date.now();
          calculateProgress();
          rafId = null;
        });
      }
    };

    // Calculate initial progress
    calculateProgress();

    // Listen to scroll events
    const target = containerRef?.current || window;
    target.addEventListener('scroll', handleScroll, { passive: true });

    // Also recalculate on resize
    window.addEventListener('resize', handleScroll, { passive: true });

    return () => {
      target.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [containerRef, calculateProgress, throttleMs]);

  return { progress };
}
